import {
    Button as MuiButton,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Typography,
} from "@material-ui/core";
import {Add, Cancel, CheckCircle, Visibility} from "@material-ui/icons";
import {
    Button,
    Datagrid,
    DeleteButton,
    Edit,
    FunctionField,
    ListButton,
    ListContextProvider,
    Pagination,
    SaveButton,
    SimpleForm,
    TextField,
    TextInput,
    Toolbar,
    TopToolbar,
    useList,
    useRecordContext,
    useRedirect
} from "react-admin";
import {useEffect, useMemo, useState} from "react";
import {
    useQuizAssessmentQuestionBankDataProvider
} from "../../providers/data-provider/quiz-assessment-question-bank.data-provider";
import {Alert} from "@material-ui/lab";


const CustomToolbar = props => {
    return  (
        <Toolbar {...props} style={{justifyContent: 'space-between'}}>
            <SaveButton disabled={props.record?.isGeneral}/>
            <DeleteButton
                disabled={props.record?.isGeneral}
                confirmTitle="Excluir este banco de questões?"
                confirmContent="Esta ação não poderá ser desfeita."
                mutationMode="pessimistic"
            />
        </Toolbar>
    )
};

const EditActions = ({ basePath }) => (
    <TopToolbar>
        <ListButton basePath={basePath} label="Cancelar" icon={<Cancel />} />
    </TopToolbar>
);

const QuestionDetailsModal = ({ record, open, onClose }) => (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth onClick={e => e.stopPropagation()}>
        <DialogTitle>{record.code ? `Questão #${record.code}` : 'Detalhes da Questão'}</DialogTitle>
        <DialogContent dividers>
            <Typography variant="subtitle2" color="textSecondary">Área do Direito</Typography>
            <Typography variant="body1" gutterBottom>{record.theme?.name || '—'}</Typography>

            <Divider style={{ margin: '12px 0' }} />

            <Typography variant="subtitle2" color="textSecondary">Enunciado</Typography>
            <Typography variant="body1" style={{ whiteSpace: 'pre-wrap' }} gutterBottom>{record.question}</Typography>

            {record.tags?.length > 0 && (
                <>
                    <Divider style={{ margin: '12px 0' }} />
                    <Typography variant="subtitle2" color="textSecondary" gutterBottom>Tags</Typography>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        {record.tags.map(tag => <Chip key={tag} label={tag} size="small" />)}
                    </div>
                </>
            )}

            {record.options?.length > 0 && (
                <>
                    <Divider style={{ margin: '12px 0' }} />
                    <Typography variant="subtitle2" color="textSecondary" gutterBottom>Opções</Typography>
                    {record.options.map((opt, idx) => (
                        <div key={idx} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 8 }}>
                            {opt.isCorrect
                                ? <CheckCircle style={{ color: '#4caf50', marginTop: 2 }} fontSize="small" />
                                : <Cancel style={{ color: '#bdbdbd', marginTop: 2 }} fontSize="small" />
                            }
                            <Typography variant="body2">{opt.optionText}</Typography>
                        </div>
                    ))}
                </>
            )}

            {record.comments && (
                <>
                    <Divider style={{ margin: '12px 0' }} />
                    <Typography variant="subtitle2" color="textSecondary">Comentários</Typography>
                    <Typography variant="body2" style={{ whiteSpace: 'pre-wrap' }}>{record.comments}</Typography>
                </>
            )}
        </DialogContent>
        <DialogActions>
            <MuiButton onClick={onClose} color="primary">Fechar</MuiButton>
        </DialogActions>
    </Dialog>
)

const ViewQuestionButton = () => {
    const record = useRecordContext()
    const [open, setOpen] = useState(false)

    return (
        <>
            <Button onClick={e => { e.stopPropagation(); setOpen(true); }}>
                <Visibility />
            </Button>
            <QuestionDetailsModal record={record} open={open} onClose={() => setOpen(false)} />
        </>
    )
}

const QuestionButton = () => {
    const record = useRecordContext()
    const redirect = useRedirect()
    const { id } = record
    const handleClick = () => {
        redirect(`/question-selector/${id}`)
    }

    return (
        <Button label={record.questions?.length ? 'Editar Questões' : `Selecionar questões`} variant={'contained'} onClick={handleClick}>
            <Add />
        </Button>
    )
}

const TagsField = ({ record }) => {
    return (
        record.tags.map((tag) => {
            return <Chip label={tag} />
        })
    )
}

export function QuizAssessmentQuestionBankEdit(props) {
    const [record, setRecord] = useState(undefined)

    const { finQuestionBank } = useQuizAssessmentQuestionBankDataProvider();
    
    useEffect(() => {
        finQuestionBank(props.id).then(res => setRecord(res.data))
    }, [finQuestionBank, props.id]);

    const readOnly = useMemo(() => {
        if (!record) {
            return true;
        }
        return record?.isGeneral;
    }, [record])

    return (
        <>
            <Edit
                mutationMode="pessimistic"
                actions={<EditActions />}
                {...props}
                transform={data => ({ ...data, questions: undefined })}

            >
                <SimpleForm toolbar={<CustomToolbar />}>

                    <TextInput source='name' label='Nome' fullWidth required disabled={readOnly} />
                    <TextInput source="description" label="Descrição" multiline minRows={2} fullWidth disabled={readOnly} />
                    <QuestionBank {...props} />
                    {
                        !readOnly && (
                            <QuestionButton />
                        )
                    }

                    <Alert severity={'warning'} style={{ width: '100%', boxSizing: 'border-box', marginTop: 20 }}>
                        Todas as alterações só surtirão efeito nas futuras avaliações criadas. Caso necessite, crie novas avaliações para que as alterações entrem em vigor.
                    </Alert>

                </SimpleForm>
            </Edit>
        </>
    )
}

const QuestionBank = props => {
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(5);
    const data = useMemo(() => props.record.questions, [props.record.questions]);

    const listContext = useList({
        data: data,
        ids: data.map((r) => r.id),
        loading: false,
        loaded: true,
        page,
        perPage,
        total: data.length,   // total of the full dataset
        setPage,
        setPerPage,
    });

    return (
        <ListContextProvider value={listContext}>
            <Datagrid>
                <TextField source="code" label="Código" />
                <FunctionField
                    source="question"
                    label="Enunciado"
                    render={rec => rec.question.length > 150 ? rec.question?.substring(0, 150) + '...' : rec.question}
                />
                <TextField source="theme.name" label="Área do direito" />
                <TagsField label="Tags" />
                <ViewQuestionButton  />
            </Datagrid>
            <Pagination rowsPerPageOptions={[3, 5, 8]} />
        </ListContextProvider>
    )
}
